import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import {
  AnswerInput,
  ClaimAnnotationDto,
  ConflictDetail,
  ConflictQuestion,
  DataExtractionConflictService,
} from '../../../services/data-extraction-conflict.service';
import { ProjectService } from '../../../services/project.service';
import { ToastService } from '../../../services/toast.service';
import { Globals } from '../../../global/globals';
import { httpErrorToMessage } from '../../../project-corpus/adapters/http-error-message';
import { HasUnsavedChanges } from '../../../guards/unsaved-changes.guard';
import { DataExtractionQuestionnaireComponent } from '../data-extraction-questionnaire/data-extraction-questionnaire.component';
import { ReviewerAnnotationsModalComponent } from './reviewer-annotations-modal.component';
import {
  ExtractionQuestion,
  QuestionType,
} from '../../../extraction-schema/domain/extraction-question.model';
import { AnswerRow, SKIPPED_VALUE } from '../../../extraction-answers/domain/types';
import { Claim } from '../../../claim-annotator/domain/types';

type Choice = 'acceptA' | 'acceptB' | 'neither' | null;

/**
 * PI per-paper conflict-resolution detail page. Opens the conflict on init
 * (acquiring the PI lock), lets the PI accept reviewer A's or B's answer or
 * enter a custom answer ("Neither") for each conflicted question, then resolves
 * with the staged answers. In a COMPLETED project the page is fully read-only.
 */
@Component({
  selector: 'app-project-data-extraction-conflict-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DataExtractionQuestionnaireComponent, ReviewerAnnotationsModalComponent],
  templateUrl: './project-data-extraction-conflict-detail.component.html',
})
export class ProjectDataExtractionConflictDetailComponent implements OnInit, HasUnsavedChanges {
  private readonly conflictService = inject(DataExtractionConflictService);
  private readonly projectService = inject(ProjectService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);

  readonly QuestionType = QuestionType;

  private projectId = 0;
  private ptpId = 0;
  private groupId: string | null = null;

  readonly modalClaimFilter = computed(() => {
    const q = this.modalQuestion();
    return q ? `q:${q.questionId}` : '';
  });

  readonly modalDocumentId = computed(() => {
    const d = this.detail();
    return d ? `paper:${d.paper.paperId}` : '';
  });

  /** Numeric project id for the reviewer-annotations modal input. */
  get projectIdForModal(): number {
    return this.projectId;
  }

  readonly detail = signal<ConflictDetail | null>(null);
  readonly loading = signal(true);
  readonly errorMsg = signal<string | null>(null);
  readonly resolving = signal(false);
  readonly resolveError = signal<string | null>(null);

  /** Annotations (with OCR text) loaded for each reviewer, keyed by reviewer id. */
  readonly reviewerAnnotations = signal<Map<number, ClaimAnnotationDto[]>>(new Map());

  private readonly choice = signal(new Map<number, Choice>());
  private readonly staged = signal(new Map<number, string>());
  private readonly neitherErrors = signal(new Map<number, string>());
  readonly touched = signal(false);

  // PDF / modal state
  private readonly pdfCache = new Map<number, Blob>();
  readonly pdfLoadError = signal<string | null>(null);
  readonly pdfLoading = signal(false);
  readonly modalOpen = signal(false);
  readonly modalPdf = signal<Blob | null>(null);
  readonly modalQuestion = signal<ConflictQuestion | null>(null);

  readonly conflictedQuestions = computed(
    () => this.detail()?.questions.filter((q) => q.conflict) ?? [],
  );

  readonly canResolve = computed(() => {
    const d = this.detail();
    if (!d || d.readOnly) return false;
    return this.conflictedQuestions().every((q) => {
      const v = this.staged().get(q.questionId);
      return v != null && this.isAnswered(q.type, v) && !this.neitherErrors().has(q.questionId);
    });
  });

  readonly blockedReason = computed<string | null>(() => {
    if (this.canResolve()) return null;
    const remaining = this.conflictedQuestions().filter((q) => {
      const v = this.staged().get(q.questionId);
      return !(v != null && this.isAnswered(q.type, v) && !this.neitherErrors().has(q.questionId));
    }).length;
    if (remaining === 0) return null;
    return `Choose an answer for each conflicted question (${remaining} remaining).`;
  });

  readonly claims = computed<Claim[]>(() =>
    (this.detail()?.questions ?? []).map((q) => ({ id: `q:${q.questionId}`, text: q.text })),
  );

  ngOnInit(): void {
    this.projectId = Number(this.route.parent?.snapshot.paramMap.get('projectId'));
    this.ptpId = Number(this.route.snapshot.paramMap.get('paperToProjectId'));
    this.loadProjectGroup();
    this.open();
  }

  private open(): void {
    this.loading.set(true);
    this.errorMsg.set(null);
    this.conflictService.openConflict(this.projectId, this.ptpId).subscribe({
      next: (d) => {
        this.detail.set(d);
        this.seedFromPiValues(d);
        this.loading.set(false);
        this.loadReviewerAnnotations(d);
      },
      error: (err: unknown) => {
        this.errorMsg.set(this.backendMessage(err, () => httpErrorToMessage(err)));
        this.loading.set(false);
      },
    });
  }

  private loadReviewerAnnotations(d: ConflictDetail): void {
    Promise.all([
      firstValueFrom(
        this.conflictService.getReviewerAnnotations(
          this.projectId,
          d.paper.paperId,
          d.reviewerA.reviewerId,
        ),
      ),
      firstValueFrom(
        this.conflictService.getReviewerAnnotations(
          this.projectId,
          d.paper.paperId,
          d.reviewerB.reviewerId,
        ),
      ),
    ])
      .then(([a, b]) => {
        this.reviewerAnnotations.update((m) => {
          const next = new Map(m);
          next.set(d.reviewerA.reviewerId, a);
          next.set(d.reviewerB.reviewerId, b);
          return next;
        });
      })
      .catch(() => {
        // Non-critical; OCR text display is a best-effort affordance.
      });
  }

  /** Returns OCR text lines for a given question and the given reviewer's annotations. */
  ocrTextLines(reviewerId: number, questionId: number): string[] {
    const anns = this.reviewerAnnotations().get(reviewerId) ?? [];
    const claimId = `q:${questionId}`;
    return anns.filter((a) => a.claimRef === claimId && a.ocrText).map((a) => a.ocrText!);
  }

  /**
   * Pre-populate the staging signals from each conflicted question's previously
   * saved PI value so that reopening an already-resolved (but still editable)
   * paper does not force the PI to re-pick every answer. Seeding a saved value
   * is not a new unsaved edit, so `touched` is left untouched.
   */
  private seedFromPiValues(d: ConflictDetail): void {
    for (const q of d.questions) {
      if (!q.conflict || q.piValue == null || q.piValue === '') continue;
      const value = q.piValue;
      let choice: Choice;
      if (value === q.reviewerAValue) {
        choice = 'acceptA';
      } else if (value === q.reviewerBValue) {
        choice = 'acceptB';
      } else {
        choice = 'neither';
      }
      this.choice.update((m) => new Map(m).set(q.questionId, choice));
      this.staged.update((m) => new Map(m).set(q.questionId, value));
    }
  }

  /**
   * Surface the backend's natural-language reason (ProblemDetail `detail`, or
   * `message`) when present, falling back to the shared generic mapper (which
   * still distinguishes an unreachable server). Avoids editing the shared helper.
   */
  private backendMessage(err: unknown, fallback: () => string): string {
    const body = (err as { error?: { detail?: unknown; message?: unknown } })?.error;
    const detail = body?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    const message = body?.message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
    return fallback();
  }

  private loadProjectGroup(): void {
    this.projectService.getProjectById(String(this.projectId)).subscribe({
      next: (p) => (this.groupId = p?.groupId ?? null),
      error: () => (this.groupId = null),
    });
  }

  // --- staging helpers ------------------------------------------------------

  choiceFor(questionId: number): Choice {
    return this.choice().get(questionId) ?? null;
  }

  isNeither(questionId: number): boolean {
    return this.choiceFor(questionId) === 'neither';
  }

  stagedValue(questionId: number): string {
    return this.staged().get(questionId) ?? '';
  }

  neitherError(questionId: number): string | null {
    return this.neitherErrors().get(questionId) ?? null;
  }

  acceptA(q: ConflictQuestion): void {
    this.setChoice(q.questionId, 'acceptA', q.reviewerAValue ?? '');
  }

  acceptB(q: ConflictQuestion): void {
    this.setChoice(q.questionId, 'acceptB', q.reviewerBValue ?? '');
  }

  neither(q: ConflictQuestion): void {
    this.choice.update((m) => new Map(m).set(q.questionId, 'neither'));
    this.touched.set(true);
    // Reveal the widget with an empty staged value and validate immediately.
    this.onNeither(q, this.staged().get(q.questionId) ?? '');
  }

  onNeither(q: ConflictQuestion, value: string): void {
    this.staged.update((m) => new Map(m).set(q.questionId, value));
    this.touched.set(true);
    const error = this.validate(q, value);
    this.neitherErrors.update((m) => {
      const next = new Map(m);
      if (error) next.set(q.questionId, error);
      else next.delete(q.questionId);
      return next;
    });
  }

  private setChoice(questionId: number, choice: Choice, value: string): void {
    this.choice.update((m) => new Map(m).set(questionId, choice));
    this.staged.update((m) => new Map(m).set(questionId, value));
    this.neitherErrors.update((m) => {
      const next = new Map(m);
      next.delete(questionId);
      return next;
    });
    this.touched.set(true);
  }

  private validate(q: ConflictQuestion, value: string): string | null {
    // "No answer can be found" is a complete PI choice for every question type.
    if (value === SKIPPED_VALUE) return null;
    const type = q.type as QuestionType;
    if (type === QuestionType.MULTI_CHOICE) {
      let arr: unknown;
      try {
        arr = JSON.parse(value || '[]');
      } catch {
        return 'Select at least one option.';
      }
      if (q.required && (!Array.isArray(arr) || arr.length === 0)) {
        return 'Select at least one option.';
      }
      return null;
    }
    if (type === QuestionType.NUMERIC) {
      if (!value.trim()) return q.required ? 'Enter a number.' : null;
      if (Number.isNaN(Number(value))) return 'Enter a valid number.';
      return null;
    }
    if (q.required && !value.trim()) return 'Enter an answer.';
    return null;
  }

  private isAnswered(type: string, value: string): boolean {
    if (value === SKIPPED_VALUE) return true;
    if (type === QuestionType.MULTI_CHOICE) {
      try {
        const arr = JSON.parse(value || '[]');
        return Array.isArray(arr) && arr.length > 0;
      } catch {
        return false;
      }
    }
    return value.trim().length > 0;
  }

  // --- questionnaire mapping ------------------------------------------------

  /**
   * Friendly label for a question type enum name, mirroring the questionnaire
   * component's typeLabel. ConflictQuestion.type is a plain string.
   */
  typeLabel(type: string): string {
    switch (type) {
      case QuestionType.FULL_TEXT:
        return 'Text';
      case QuestionType.NUMERIC:
        return 'Number';
      case QuestionType.BOOLEAN:
        return 'Yes / No';
      case QuestionType.SINGLE_CHOICE:
        return 'Single choice';
      case QuestionType.MULTI_CHOICE:
        return 'Multiple choice';
      default:
        return type;
    }
  }

  asQuestion(q: ConflictQuestion): ExtractionQuestion {
    return {
      id: q.questionId,
      text: q.text,
      type: q.type as QuestionType,
      required: q.required,
      orderIndex: 0,
      options: q.options ?? [],
    };
  }

  answerRow(questionId: number, value: string | null): AnswerRow[] {
    return [{ questionId, value: value ?? '' }];
  }

  // --- actions --------------------------------------------------------------

  resolve(): void {
    if (!this.canResolve() || this.resolving()) return;
    const answers: AnswerInput[] = this.conflictedQuestions().map((q) => ({
      questionId: q.questionId,
      value: this.staged().get(q.questionId) ?? '',
    }));
    this.resolving.set(true);
    this.resolveError.set(null);
    this.conflictService.resolveConflict(this.projectId, this.ptpId, answers).subscribe({
      next: () => {
        this.resolving.set(false);
        this.touched.set(false);
        this.toast.success('Conflict resolved', 'The PI answer has been recorded.');
        void this.router.navigate(['../'], { relativeTo: this.route });
      },
      error: (err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          void this.router.navigate(['/login']);
          return;
        }
        this.resolveError.set(this.backendMessage(err, () => httpErrorToMessage(err)));
        this.resolving.set(false);
      },
    });
  }

  /**
   * Used by the route's CanDeactivate guard so navigating away (Back button, stage tabs, browser
   * back) prompts before discarding staged resolution answers. A successful resolve clears
   * `touched` first, so it never prompts on the happy path.
   */
  hasUnsavedChanges(): boolean {
    return this.touched();
  }

  back(): void {
    void this.router.navigate(['../'], { relativeTo: this.route });
  }

  async viewAnnotations(q: ConflictQuestion): Promise<void> {
    const d = this.detail();
    if (!d || this.pdfLoading()) return;
    this.pdfLoadError.set(null);
    const paper = d.paper;
    try {
      let blob = this.pdfCache.get(paper.paperToProjectId);
      if (!blob) {
        if (!this.groupId || paper.pdfId == null) {
          this.pdfLoadError.set('No PDF is available for this paper.');
          return;
        }
        const url = `${this.globals.backendUri}/groups/${this.groupId}/pdfs/${paper.pdfId}/content`;
        this.pdfLoading.set(true);
        blob = await firstValueFrom(this.http.get(url, { responseType: 'blob' }));
        this.pdfCache.set(paper.paperToProjectId, blob);
      }
      this.modalPdf.set(blob);
      this.modalQuestion.set(q);
      this.modalOpen.set(true);
    } catch (err: unknown) {
      this.pdfLoadError.set(httpErrorToMessage(err));
    } finally {
      this.pdfLoading.set(false);
    }
  }

  onModalOpenChange(open: boolean): void {
    this.modalOpen.set(open);
  }

  // --- unsaved-changes guard ------------------------------------------------

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.touched() && !this.resolving()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
}
